import { z } from "zod";
import type { Plan } from "./enums";
import { PLAN_LIMITS } from "./plan-limits";

/**
 * Email reminders, weekly digest and the calendar feed (iOS NotificationScheduler + CalendarExport rules, on the
 * web). Opt-in service mail about the recipient's own weddings — never marketing (markedsføringsloven § 15).
 */
export const REMINDER = {
  /** Local time the daily mail goes out (iOS `reminderHour`). */
  hour: 9,
  /** Tasks: a reminder this many days ahead and on the day (iOS `Lead.task`). */
  taskLeadDays: 3,
  /** Budget lines and vendor deposits/balances: this many days ahead and on the day (iOS `Lead.money`). */
  moneyLeadDays: 7,
  /** RSVP reply date: a week before and the day before (web addition). */
  rsvpLeadDays: [7, 1] as const,
  /** Digest: payments due within this many days. */
  digestPaymentDays: 30,
} as const;

export const ISO_WEEKDAYS = [1, 2, 3, 4, 5, 6, 7] as const;

export const updateNotificationPrefsSchema = z.object({
  reminders: z.boolean().optional(),
  /** ISO weekday 1 = Monday … 7 = Sunday; null = no digest. */
  digestWeekday: z.number().int().min(1).max(7).nullable().optional(),
  /** IANA zone from the browser; validated by the API (unknown → refused). */
  timezone: z.string().trim().min(1).max(64).nullable().optional(),
  nudgeDismissed: z.literal(true).optional(),
});
export type UpdateNotificationPrefs = z.infer<typeof updateNotificationPrefsSchema>;

export interface NotificationPrefsDTO {
  reminders: boolean;
  digestWeekday: number | null;
  timezone: string | null;
  /** https://…/api/cal/<token>.ics — a secret link; null until created. */
  calendarUrl: string | null;
  webcalUrl: string | null;
  emailsThisMonth: number;
  /** The plan's allowance; null = custom; 0 = not included (Free). */
  emailsPerMonth: number | null;
  nudgeDismissed: boolean;
}

/** May this plan get reminder mail at all, and is there room left this month? */
export function checkEmailQuota(plan: Plan, sentThisMonth: number): { ok: true } | { ok: false; limit: number } {
  const limit = PLAN_LIMITS[plan].emailsPerMonth;
  if (limit === null) return { ok: true };
  return sentThisMonth < limit ? { ok: true } : { ok: false, limit };
}

/** `YYYY-MM-DD` plus n days (calendar arithmetic on the date itself, no time zone involved). */
export function addDaysKey(dateKey: string, n: number): string {
  const [y, m, d] = dateKey.split("-").map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
}

/** ISO 8601 week of a `YYYY-MM-DD` date, as `YYYY-Www` (Thursday rule). */
export function isoWeekKey(dateKey: string): string {
  const [y, m, d] = dateKey.split("-").map(Number) as [number, number, number];
  const date = new Date(Date.UTC(y, m - 1, d));
  const day = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  const week = Math.ceil(((date.getTime() - yearStart.getTime()) / 86_400_000 + 1) / 7);
  return `${date.getUTCFullYear()}-W${String(week).padStart(2, "0")}`;
}
